import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { FormControl, Checkbox, ToggleButton, InputLabel, MenuItem, Select, Chip, Tooltip, Paper, Grid } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { List, Map } from 'immutable';

import Button from 'src/component/UI/Button';
import FloatingActionBar from 'src/component/UI/FloatingActionBar/FloatingActionBar';
import ExportMenu from 'src/component/UI/ExportMenu';
import CsvIcon from 'src/assets/icons/csv.svg';
import PdfIcon from 'src/assets/icons/pdf.svg';
import FilterBar from 'src/component/UI/FilterBar';
import DataTable from 'src/component/UI/DataTable';
import PermissionFence from 'src/component/PermissionFence';
import CommissioningStatusIcon from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningStatusIcon';
import ReportModals from './reportModalRegistry';
import FieldReportTypeSelectDialog from './FieldReportTypeSelectDialog';
import LinkDeviceDialog from './LinkDeviceDialog';

import {
  fetchFieldReports,
  openCheckSheet,
  closeCheckSheet,
  linkDeviceToCheckSheet,
  downloadFieldReportPdf,
  downloadBulkFieldReportPdf,
  downloadFieldReportSummaryPdf,
  fetchReportsCsvExport,
} from 'src/module/checkSheet/action';
import {
  getFieldReports,
  getFieldReportsLoading,
  getFieldReportsTotal,
  getFieldReportsTotalPages,
  getFieldReportsLastLinkSuccess,
  getFieldReportsLastSaveSuccess,
  getBulkFieldReportPdfLoading,
  getSummaryFieldReportPdfLoading,
  getFieldReportPdfLoadingByReportId,
  getExportReportsLoading,
} from 'src/module/checkSheet/selector';
import { usePDFServices } from 'src/hooks/useReportData';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/VisibilitySharp';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import { formatDate } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';
import toast from 'src/utils/toast';
import variables from 'src/style/variable/variables.module.scss';
import './FieldReportListScreen.scss';
import { FIELD_REPORT_TYPE_LABEL_MAP, FIELD_REPORT_DEVICE_TYPE_MAP, COMMISSIONING_STATUS_OPTIONS, FieldReportCategory, FieldReportTypeDef } from './fieldReportTypes';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';


const pdfIconStyles = { color: variables.primarySolid };

export type FieldReportListConfig = {
  /** Server-side category. Sent on every list/CSV/summary request; narrows which types load. */
  category: FieldReportCategory;
  /** Types offered in the type filter and the create dialog, in display order. */
  types: FieldReportTypeDef[];
  /** i18n key for the browser title and the page heading. */
  titleKey: string;
  /** i18n key for the create dialog's heading. */
  createDialogTitleKey: string;
  /**
   * Commissioning status column and status filter. False for audits, which never write
   * commissioning_status — all four audit forms pass requiresStatus={false}.
   */
  showStatus: boolean;
  /** DataTable page-size preference key. Distinct per screen so the two don't fight over it. */
  tableId: string;
  /** Root element id, for e2e hooks and per-screen escape-hatch selectors. */
  rootId: string;
  /** Column widths, with and without the bulk-select checkbox column. */
  columnWidths: { withSelect: string[]; withoutSelect: string[] };
};


function PdfDownloadButton ({ reportId, checkSheetType, deviceTag }: { reportId: string; checkSheetType: string; deviceTag: string }) {
  const dispatch = useDispatch();
  const loading = useSelector(getFieldReportPdfLoadingByReportId(reportId));
  return (
    <Button
      compact
      icon={<FileDownloadIcon style={pdfIconStyles} />}
      spinner={<LoadingSpinner color={variables.primarySolid} size={16} />}
      loading={loading}
      disabled={loading}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(downloadFieldReportPdf(reportId, checkSheetType, deviceTag));
      }}
    />
  );
}

export default function FieldReportListScreen ({ config }: { config: FieldReportListConfig }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [page, setPage] = useState(0);
  const userPagePreference = useSelector(getTablePagePreferences(config.tableId));
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [sort, setSort] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const selectedFilters: any = useSelector(getDevicesSelectedFilters);

  const getFilterParams = () => ({
    unit: selectedFilters.get('unit') || undefined,
    system: selectedFilters.get('system') || undefined,
    commodity: selectedFilters.get('commodity') || undefined,
    location: selectedFilters.get('location') || undefined,
    priority: selectedFilters.get('priority') || undefined,
    controllerId: selectedFilters.get('controller') || undefined,
    deadleg: selectedFilters.get('deadleg') === 'Yes' ? 'true' : selectedFilters.get('deadleg') === 'No' ? 'false' : undefined,
    deviceGroup: selectedFilters.get('deviceGroup') || undefined,
  });

  // The category is what keeps this screen's list disjoint from the other one's.
  const getFetchParams = () => ({
    page,
    pageSize,
    sort,
    category: config.category,
    type: typeFilter || undefined,
    status: (config.showStatus && statusFilter) || undefined,
    unlinkedOnly: unlinkedOnly || undefined,
    ...getFilterParams(),
  });

  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);

  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());

  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkReportId, setLinkReportId] = useState(null);
  const [linkReportType, setLinkReportType] = useState(null);


  const lastLinkSuccess = useSelector(getFieldReportsLastLinkSuccess);
  const lastSaveSuccess = useSelector(getFieldReportsLastSaveSuccess);

  // Refetch the list when a report is actually created or updated, rather than on modal close.
  // This is tied to the data mutation (mirrors lastLinkSuccess for device linking) so the
  // refresh is reliable and does not fire on cancel.
  useEffect(() => {
    if (lastSaveSuccess > 0) {
      dispatch(fetchFieldReports(getFetchParams()));
    }
  }, [lastSaveSuccess]);

  useEffect(() => {
    if (lastLinkSuccess > 0) {
      dispatch(fetchFieldReports(getFetchParams()));
    }
  }, [lastLinkSuccess]);

  const reports = useSelector(getFieldReports);
  const loading = useSelector(getFieldReportsLoading);
  const total = useSelector(getFieldReportsTotal);
  const totalPages = useSelector(getFieldReportsTotalPages);

  const { usePDFServicesValue: pdfServicesEnabled } = usePDFServices();
  const bulkDownloading = useSelector(getBulkFieldReportPdfLoading);
  const summaryDownloading = useSelector(getSummaryFieldReportPdfLoading);

  const BULK_DOWNLOAD_WARN_LIMIT = 10;
  const BULK_DOWNLOAD_HARD_LIMIT = 20;

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
  };

  useEffect(() => {
    dispatch(fetchFieldReports(getFetchParams()));
  }, [page, pageSize, sort, typeFilter, statusFilter, unlinkedOnly, selectedFilters, dispatch]);

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };



  const onClickCreate = () => {
    setShowTypeDialog(true);
  };

  const handleTypeSelected = (type, deviceId) => {
    setSelectedType(type);
    setSelectedDeviceId(deviceId);
    setSelectedReportId(null);
    setShowTypeDialog(false);
    // Jump back to the first page so the newly-saved report (sorted newest-first) is visible
    // after the post-save refetch instead of being stranded on a later page.
    setPage(0);
    dispatch(openCheckSheet(type));
  };

  const handleClose = (type) => {
    dispatch(closeCheckSheet(type));
    setSelectedReportId(null);
    setSelectedDeviceId(null);
    setSelectedType(null);
    dispatch(fetchFieldReports(getFetchParams()));
  };

  const handleToggleSelect = useCallback((reportId: string) => {
    setSelectedReportIds(prev => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedReportIds(new Set(reports.map((r: any) => r.get('id')).toArray()));
    } else {
      setSelectedReportIds(new Set());
    }
  }, [reports]);

  const prevBulkDownloading = useRef(false);
  useEffect(() => {
    if (prevBulkDownloading.current && !bulkDownloading) {
      setSelectedReportIds(new Set());
    }
    prevBulkDownloading.current = bulkDownloading;
  }, [bulkDownloading]);

  const handleBulkDownload = () => {
    if (selectedReportIds.size > BULK_DOWNLOAD_HARD_LIMIT) {
      toast.error(t('fieldReport.downloadLimitExceeded', { limit: BULK_DOWNLOAD_HARD_LIMIT }));
      return;
    }
    dispatch(downloadBulkFieldReportPdf(Array.from(selectedReportIds)));
  };

  const handleSummaryExport = () => {
    const rawParams: Record<string, any> = {
      category: config.category,
      type: typeFilter || undefined,
      status: (config.showStatus && statusFilter) || undefined,
      unlinkedOnly: unlinkedOnly ? 'true' : undefined,
      order: sort || undefined,
      ...getFilterParams(),
    };
    const params = Object.fromEntries(
      Object.entries(rawParams).filter(([, v]) => v !== undefined && v !== null)
    );
    dispatch(downloadFieldReportSummaryPdf(params));
  };

  const csvExportLoading = useSelector(getExportReportsLoading);

  const handleExportCsvClick = () => {
    dispatch(fetchReportsCsvExport(selectedFilters, Map({
      category: config.category,
      type: typeFilter,
      status: config.showStatus ? statusFilter : '',
      unlinkedOnly,
    })));
  };


  const onClickLinkDevice = (e, reportId, reportType) => {
    e.stopPropagation();
    setLinkReportId(reportId);
    setLinkReportType(FIELD_REPORT_DEVICE_TYPE_MAP[reportType]);
    setShowLinkDialog(true);
  };

  const handleLinkDevice = (deviceId) => {
    dispatch(linkDeviceToCheckSheet(linkReportId, deviceId));
    setShowLinkDialog(false);
    setLinkReportId(null);
    setLinkReportType(null);
  };

  // MUI injects styles via CSS-in-JS (emotion) at runtime with higher specificity than external SCSS.
  // Using sx prop instead of className ensures our overrides win — this is the correct way to style MUI components.
  const checkboxStyles = {
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': {
      fontSize: 22,
      borderRadius: '6px',
    },
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&.Mui-checked': {
      color: '#1976d2',
      '& .MuiSvgIcon-root': {
        filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))',
      },
    },
    '&.MuiCheckbox-indeterminate': {
      color: '#1976d2',
    },
  };

  const columns: any = useMemo(() => [
    ...(pdfServicesEnabled ? [{
      id: 'select',
      Header: () => (
        <Checkbox
          checked={reports.size > 0 && selectedReportIds.size === reports.size}
          indeterminate={selectedReportIds.size > 0 && selectedReportIds.size < reports.size}
          onChange={(e) => handleSelectAll(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          sx={checkboxStyles}
        />
      ),
      accessor: (row) => row.get('id'),
      Cell: (row: any) => (
        <Checkbox
          checked={selectedReportIds.has(row.cell.value)}
          onChange={() => handleToggleSelect(row.cell.value)}
          onClick={(e) => e.stopPropagation()}
          sx={checkboxStyles}
        />
      ),
    }] : []),
    {
      id: 'check_sheet_type',
      Header: t('fieldReport.formType'),
      accessor: (row) => row.get('check_sheet_type'),
      Cell: (row: any) => t(FIELD_REPORT_TYPE_LABEL_MAP[row.cell.value] || row.cell.value),
    },
    {
      id: 'device_tag',
      Header: t('fieldReport.deviceTag'),
      accessor: (row) => row.get('device_tag'),
      Cell: (row: any) => {
        if (row.cell.value) {
          return ( <DeviceLinkCombo device={row.cell.row.original} />);
        }
        const reportTag = row.cell.row.original.get('report_tag');
        return (
          <div className='field-report-row-actions'>
            {reportTag && <span className='field-report-tag'>{reportTag}</span>}
            <Chip
              icon={<LinkIcon />}
              label={t('fieldReport.linkDevice')}
              variant='outlined'
              size='small'
              onClick={(e) => onClickLinkDevice(e, row.cell.row.original.get('id'), row.cell.row.original.get('check_sheet_type'))}
              className={'field-report-linkChip'}
            />
          </div>
        );
      },
    },
    ...(config.showStatus ? [{
      id: 'commissioning_status',
      Header: t('common.commissioningStatus'),
      accessor: (row) => row.get('commissioning_status'),
      Cell: (row: any) => <CommissioningStatusIcon status={row.cell.value} />,
    }] : []),
    {
      id: 'created_by',
      Header: t('testingReports.createdBy'),
      accessor: (row) => row.get('first_name'),
      Cell: (row: any) => `${row.cell.row.original.get('first_name') || ''} ${row.cell.row.original.get('last_name') || ''}`.trim(),
    },
    {
      id: 'created_at',
      Header: t('testingReports.createdAt'),
      accessor: (row) => row.get('created_at'),
      Cell: (row: any) => row.cell.value ? formatDate(row.cell.value) : '',
    },
    {
      id: 'open',
      Header: '',
      sortable: false,
      accessor: (row) => row.get('id'),
      Cell: (row: any) => (
        <div className='field-report-row-actions'>
          <Tooltip title={t('fieldReport.viewDetails')} placement='top'>
            <span>
              <Button
                compact
                icon={<VisibilityIcon style={{ color: variables.primaryFlat }} />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const original = row.cell.row.original;
                  const type = original.get('check_sheet_type');
                  const id = original.get('id');
                  setSelectedReportId(id);
                  setSelectedDeviceId(original.get('device_id') || null);
                  setSelectedType(type);
                  dispatch(openCheckSheet(type));
                }}
              />
            </span>
          </Tooltip>

          {pdfServicesEnabled && (
            <Tooltip title={t('fieldReport.download')} placement='top'>
              <span>
                <PdfDownloadButton
                  reportId={row.cell.row.original.get('id')}
                  checkSheetType={row.cell.row.original.get('check_sheet_type')}
                  deviceTag={row.cell.row.original.get('device_tag') || ''}
                />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
  ], [t, pdfServicesEnabled, selectedReportIds, handleToggleSelect, handleSelectAll, reports, config.showStatus]);

  const showActionBar = pdfServicesEnabled && selectedReportIds.size > 0;
  const selectionCount = selectedReportIds.size;
  const selectionLabel = selectionCount === 1 ? t('fieldReport.report') : t('fieldReport.reports');

  return (
    <div className='page field-report-screen' id={config.rootId}>
      <Helmet>
        <title>{t(config.titleKey)}</title>
      </Helmet>
      <FilterBar includeGrid />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <div className={'field-report-header'}>
              <div className={'field-report-header-spacer'} />
              <h2>{t(config.titleKey)}</h2>
              <div className={'field-report-header-actions'}>
                <PermissionFence can='edit-check-sheet-report'>
                  <Button cta onClick={onClickCreate}>{t('fieldReport.createNew')}</Button>
                </PermissionFence>
              </div>
            </div>
            <div className={'field-report-filters'}>
              <FormControl size='small' className={'field-report-filterControl'}>
                <InputLabel>{t('fieldReport.formType')}</InputLabel>
                <Select value={typeFilter} label={t('fieldReport.formType')} displayEmpty onChange={handleTypeFilterChange}>
                  <MenuItem value=''>{t('fieldReport.allTypes')}</MenuItem>
                  {config.types.map(({ value, labelKey }) => (
                    <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {config.showStatus && (
                <FormControl size='small' className={'field-report-filterControl'}>
                  <InputLabel>{t('common.commissioningStatus')}</InputLabel>
                  <Select value={statusFilter} label={t('common.commissioningStatus')} displayEmpty onChange={handleStatusFilterChange}>
                    <MenuItem value=''>{t('fieldReport.allStatuses')}</MenuItem>
                    {COMMISSIONING_STATUS_OPTIONS.map(({ value, labelKey }) => (
                      <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <ToggleButton
                value='unlinkedOnly'
                selected={unlinkedOnly}
                onChange={() => { setUnlinkedOnly(!unlinkedOnly); setPage(0); }}
                size='small'
                className='field-report-unlinkedOnly'
              >
                <FilterListIcon fontSize='small' className='field-report-unlinkedOnly-icon' />
                {t('fieldReport.unlinkedOnly')}
              </ToggleButton>
              <ExportMenu
                triggerClassName='field-report-toolbar-option field-report-export-btn'
                triggerLabel={t('fieldReport.exportSummary')}
                options={[
                  {
                    key: 'csv',
                    label: t('downloadCsv'),
                    icon: <img src={CsvIcon} alt='' className='export-menu-file-icon' />,
                    loading: csvExportLoading,
                    disabled: csvExportLoading,
                    onClick: handleExportCsvClick,
                  },
                  {
                    key: 'pdf',
                    label: t('downloadPdf'),
                    icon: <img src={PdfIcon} alt='' className='export-menu-file-icon' />,
                    loading: summaryDownloading,
                    disabled: summaryDownloading,
                    hidden: !pdfServicesEnabled,
                    onClick: handleSummaryExport,
                  },
                ]}
              />
            </div>
            <DataTable
              filterable={false}
              pagination
              numPages={totalPages}
              totalRecords={total}
              loading={loading}
              data={List(reports)}
              columns={columns}
              fetchPage={fetchPage}
              tableId={config.tableId}
              columnWidths={pdfServicesEnabled ? config.columnWidths.withSelect : config.columnWidths.withoutSelect}
              placeholder={<span>{t('testingReports.noReports')}</span>}
            />
          </Paper>
        </Grid>
      </Grid>

      <FieldReportTypeSelectDialog
        open={showTypeDialog}
        types={config.types}
        titleKey={config.createDialogTitleKey}
        onSelect={handleTypeSelected}
        onClose={() => setShowTypeDialog(false)}
      />

      <ReportModals
        types={config.types}
        selectedType={selectedType}
        reportId={selectedReportId}
        deviceId={selectedDeviceId}
        onClose={handleClose}
      />

      <LinkDeviceDialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onConfirm={handleLinkDevice}
        deviceType={linkReportType}
      />

      {showActionBar && selectionCount > BULK_DOWNLOAD_WARN_LIMIT && (
        <div className='field-report-bulk-warning'>
          {t('fieldReport.downloadSlowWarning')}
        </div>
      )}
      {showActionBar && (
        <FloatingActionBar
          className='field-report-floating-action-bar'
          label={`${selectionCount} ${selectionLabel} ${t('fieldReport.selected')}`}
        >
          <Button className='clear-btn' onClick={() => setSelectedReportIds(new Set())} disabled={bulkDownloading}>
            {t('common.clear')}
          </Button>
          <Button
            icon={<FileDownloadIcon style={{ fontSize: 14 }} />}
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
            loading={bulkDownloading}
          >
            {t('fieldReport.downloadSelected', { count: selectionCount })}
          </Button>
        </FloatingActionBar>
      )}
    </div>
  );
}
